// How an action's value is shown (ActionPlanImpactService on the server).
//
// - Calculated: dollars from the owner's own numbers, the only per-action
//   dollar figure.
// - Benchmark estimate: a range for a whole business category, never split
//   across its actions.
// - Directional: an action's High / Medium / Low contribution to its
//   category.
//
// Every figure carries its label, wherever it is shown.

export const IMPACT_LABELS = {
  calculated: 'Calculated from your numbers',
  range: 'Benchmark estimate',
  directional: 'Directional',
};

// The same labels, short enough for a row next to the figure.
export const SHORT_LABELS = {
  calculated: 'Calculated',
  range: 'Benchmark estimate',
  directional: 'Directional',
};

export const CONTRIBUTION_LABELS = { high: 'High impact', medium: 'Medium impact', low: 'Low impact' };

export const CATEGORY_LABELS = {
  financial: 'Financial',
  customer: 'Customer',
  structural: 'Structural',
  human: 'Human',
};

export function compactMoney(value) {
  const n = Number(value) || 0;
  if (n >= 1000000) return `$${(n / 1000000).toFixed(n >= 10000000 ? 0 : 1).replace(/\.0$/, '')}M`;
  if (n >= 1000) return `$${Math.round(n / 1000)}K`;
  return `$${Math.round(n)}`;
}

// The impact of a plan item (its metadata) or of a proposal (proposal.impact),
// in one shape: { kind, category, range: [low, high], contribution, value, calculation }.
export function impactOf(source) {
  if (!source) return null;
  if (source.impact) return normalize(source.impact);
  const m = source.metadata || source;
  if (!m.impact_kind) return null;
  return normalize({
    kind: m.impact_kind,
    category: m.category,
    category_range: m.category_range,
    contribution: m.contribution,
    value_impact: m.value_impact,
    calculation: m.impact_calculation,
  });
}

function normalize(impact) {
  const range = Array.isArray(impact.category_range) ? impact.category_range : null;
  return {
    kind: impact.kind,
    category: impact.category,
    range,
    contribution: impact.contribution,
    value: impact.kind === 'calculated' ? Number(impact.value_impact) || 0 : null,
    calculation: impact.calculation || null,
  };
}

export function categoryName(category) {
  return `${CATEGORY_LABELS[category] || category} capital`;
}

export function rangeText(range) {
  if (!range) return null;
  return `roughly ${compactMoney(range[0])}–${compactMoney(range[1])}`;
}

// The short figure and the line that says what it rests on.
export function impactSummary(impact) {
  if (!impact) return null;
  if (impact.kind === 'calculated') {
    const calc = impact.calculation;
    const detail = calc
      ? `${compactMoney(calc.ebitda_change)} a year of EBITDA × ${Number(calc.multiple).toFixed(1)}x`
      : null;
    return {
      figure: `+${compactMoney(impact.value)}`,
      label: IMPACT_LABELS.calculated,
      short: SHORT_LABELS.calculated,
      detail,
    };
  }
  return {
    figure: CONTRIBUTION_LABELS[impact.contribution] || 'Impact',
    label: IMPACT_LABELS.directional,
    short: SHORT_LABELS.directional,
    detail: impact.range
      ? `${categoryName(impact.category)}: ${rangeText(impact.range)} for the whole category (${IMPACT_LABELS.range.toLowerCase()})`
      : null,
  };
}

// The range across several actions: each category's range once (ranges are
// per category, so actions in one category share it). Pass only the actions
// still to do: a finished action's range is history, not what is in reach.
export function rangeAcross(impacts) {
  const seen = {};
  impacts.filter(Boolean).forEach((impact) => {
    if (impact.range && !seen[impact.category]) seen[impact.category] = impact.range;
  });
  const ranges = Object.values(seen);
  if (ranges.length === 0) return null;
  return [ranges.reduce((s, r) => s + r[0], 0), ranges.reduce((s, r) => s + r[1], 0)];
}
